import { cn } from "@/lib/utils";

interface RunnerLoadingProps {
  size?: "sm" | "md" | "lg";
  text?: string;
  className?: string;
}

export const RunnerLoading = ({ 
  size = "md", 
  text = "Por você, com a gente e no seu tempo.",
  className 
}: RunnerLoadingProps) => {
  const sizes = {
    sm: "w-[80px] h-[80px]",
    md: "w-[120px] h-[120px]",
    lg: "w-[140px] h-[140px]"
  };

  return (
    <div className={cn("flex flex-col items-center justify-center gap-6", className)}>
      {/* Container principal com todos os efeitos */}
      <div className="relative flex items-center justify-center">
        {/* Linhas de velocidade */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="speed-line speed-line-1" />
          <div className="speed-line speed-line-2" />
          <div className="speed-line speed-line-3" />
        </div>

        {/* Partículas de vento */}
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
          <div className="wind-particle wind-particle-1" />
          <div className="wind-particle wind-particle-2" />
          <div className="wind-particle wind-particle-3" />
          <div className="wind-particle wind-particle-4" />
          <div className="wind-particle wind-particle-5" />
        </div>

        {/* GIF da corredora com motion blur */}
        <div className="relative z-10">
          <img 
            src="/runner-loading.gif?v=5" 
            alt="Running animation"
            className={cn(
              "runner-blur object-contain",
              sizes[size]
            )}
            style={{ 
              backgroundColor: 'transparent',
              mixBlendMode: 'normal' 
            }}
          />
        </div>

        {/* Pista/chão embaixo */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-8 w-[200px]">
          <div className="track-base">
            <div className="track-line track-line-1" />
            <div className="track-line track-line-2" />
            <div className="track-line track-line-3" />
          </div>
        </div>
      </div>

      {/* Texto "Aquecendo..." */}
      <p className="runner-subtitle normal-case tracking-normal text-base font-normal text-muted-foreground/80 animate-pulse text-center max-w-[280px] mx-auto">
        {text}
      </p>

      <style>{`
        /* Override para garantir sentence case */
        .runner-subtitle {
          text-transform: none !important;
          letter-spacing: normal !important;
        }

        /* Linhas de velocidade */
        .speed-line {
          position: absolute;
          height: 2px;
          background: linear-gradient(90deg, transparent, hsl(var(--primary)) 50%, transparent);
          opacity: 0;
          animation: speedLineMove 1.2s ease-in-out infinite;
        }

        .speed-line-1 {
          width: 60px;
          top: 30%;
          left: -80px;
          animation-delay: 0s;
        }

        .speed-line-2 {
          width: 45px;
          top: 50%;
          left: -70px;
          animation-delay: 0.15s;
        }

        .speed-line-3 {
          width: 50px;
          top: 70%;
          left: -75px;
          animation-delay: 0.3s;
        }

        @keyframes speedLineMove {
          0% {
            transform: translateX(0);
            opacity: 0;
          }
          20% {
            opacity: 0.6;
          }
          100% {
            transform: translateX(180px);
            opacity: 0;
          }
        }

        /* Partículas de vento */
        .wind-particle {
          position: absolute;
          background: hsl(var(--primary) / 0.4);
          border-radius: 50%;
          opacity: 0;
          animation: windMove 1.5s ease-out infinite;
        }

        .wind-particle-1 {
          width: 4px;
          height: 4px;
          top: 25%;
          right: -20px;
          animation-delay: 0s;
        }

        .wind-particle-2 {
          width: 3px;
          height: 3px;
          top: 40%;
          right: -15px;
          animation-delay: 0.2s;
        }

        .wind-particle-3 {
          width: 5px;
          height: 5px;
          top: 60%;
          right: -25px;
          animation-delay: 0.4s;
        }

        .wind-particle-4 {
          width: 3px;
          height: 3px;
          top: 75%;
          right: -10px;
          animation-delay: 0.6s;
        }

        .wind-particle-5 {
          width: 4px;
          height: 4px;
          top: 35%;
          right: -30px;
          animation-delay: 0.8s;
        }

        @keyframes windMove {
          0% {
            transform: translate(0, 0) scale(1);
            opacity: 0;
          }
          20% {
            opacity: 0.8;
          }
          100% {
            transform: translate(100px, -20px) scale(0.3);
            opacity: 0;
          }
        }

        /* Motion blur na corredora */
        .runner-blur {
          background: transparent !important;
          filter: blur(0.3px);
          animation: runnerPulse 0.8s ease-in-out infinite;
        }
        
        .runner-blur::before,
        .runner-blur::after {
          display: none;
        }

        @keyframes runnerPulse {
          0%, 100% {
            filter: blur(0.3px);
          }
          50% {
            filter: blur(0.5px);
          }
        }

        /* Pista/chão */
        .track-base {
          position: relative;
          height: 3px;
          background: linear-gradient(90deg, 
            transparent 0%, 
            hsl(var(--muted-foreground) / 0.2) 30%, 
            hsl(var(--muted-foreground) / 0.3) 50%, 
            hsl(var(--muted-foreground) / 0.2) 70%, 
            transparent 100%
          );
          border-radius: 2px;
        }

        .track-line {
          position: absolute;
          width: 2px;
          height: 8px;
          background: hsl(var(--muted-foreground) / 0.3);
          top: -3px;
        }

        .track-line-1 {
          left: 30%;
        }

        .track-line-2 {
          left: 50%;
        }

        .track-line-3 {
          left: 70%;
        }
      `}</style>
    </div>
  );
};
